// Day Drawer's own Food Log card — deliberately NOT the shared
// calendar/components/log-cards/LogFoodLogCard.js (used as-is by the
// athlete/coach calendar Log view, kept untouched — it wraps everything in a
// `.log-card.log-card--food-log`, complete with its own colored background
// and header, and only shows each meal's kcal total and bare food names)
// nor Table's TableFoodLogPanel.js (adds a meal-totals badge row but keeps
// that same card wrapper and still lists food items as bare names). This
// drawer already has its own header/tabs ("Food log" is the tab label
// itself), so wrapping the content in another titled, colored card would be
// redundant — this renders just the meals/food items, blending in the
// density of the legacy per-day FoodLog.js table view (per-meal macro
// breakdown, per-food serving size) — not a literal <table>, just the same
// level of detail, no card chrome.
// SYNC NOTE: duplicates LogFoodLogCard's merge/sort/meal-kcal logic on
// purpose (isolation over reuse — same call already made for
// TableFoodLogPanel.js, see that file's own header comment). Keep in sync
// manually if that logic changes.
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getServingLabel, mergeFoodLogs } from "../../../../../utils/daily-stats";
import { mealOrderIndex } from "./viewTokens";

const getMealKcal = (meal) => {
  if (meal.kcal != null) return Math.round(meal.kcal);
  return Math.round((meal.proteins || 0) * 4 + (meal.carbs || 0) * 4 + (meal.fats || 0) * 9);
};

const sortMeals = (meals) =>
  [...meals].sort((a, b) => mealOrderIndex(a.name) - mealOrderIndex(b.name));

const DayDrawerFoodLogCard = ({ foodLogs }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const entry = foodLogs?.[0];

  const orderedMeals = useMemo(() => {
    if (!entry) return [];
    const hasExternalFoodLog = (entry.foodLog?.meals?.length ?? 0) > 0;
    const hasInternalFoodLog = (entry.internalFoodLog?.meals?.length ?? 0) > 0;
    const source =
      hasExternalFoodLog && hasInternalFoodLog
        ? `${entry.foodLog.source}, ${entry.internalFoodLog.source}`
        : entry.foodLog?.source || entry.internalFoodLog?.source || "";
    const mergedFoodLog = hasInternalFoodLog
      ? mergeFoodLogs(entry.foodLog, entry.internalFoodLog, source)
      : entry.foodLog;
    const meals = (mergedFoodLog?.meals || []).filter(
      (meal) => meal.name || meal.foodName || (meal.foods || []).length > 0
    );
    return sortMeals(meals);
  }, [entry]);

  if (orderedMeals.length === 0) return null;

  const kcalAbbr = t("common:units.kcalAbbr");
  const proteinAbbr = t("common:macros.proteinAbbr");
  const carbsAbbr = t("common:macros.carbsAbbr");
  const fatAbbr = t("common:macros.fatAbbr");

  return (
    <div className="log-card__body log-card__food-log-body">
      {orderedMeals.map((meal, index) => (
        <div className="log-card__food-meal" key={`${meal.name || meal.foodName}-${index}`}>
          <div className="log-card__food-meal-header">
            <span className="log-card__food-meal-name">{meal.name || meal.foodName}</span>
            <span className="log-card__food-meal-kcal">
              {getMealKcal(meal)} {kcalAbbr} · {Math.round(meal.proteins || 0)} {proteinAbbr}{" "}
              {Math.round(meal.carbs || 0)} {carbsAbbr} {Math.round(meal.fats || 0)} {fatAbbr}
            </span>
          </div>
          <ul className="log-card__food-items">
            {(meal.foods || []).map((food, foodIndex) => (
              <li className="log-card__food-item" key={`${food.name}-${foodIndex}`}>
                <div className="np-drawer__food-item-row">
                  <span>{food.name}</span>
                  {(food.servingSize || food.servingName) && (
                    <span className="np-drawer__food-item-serving">
                      {getServingLabel(food)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

DayDrawerFoodLogCard.propTypes = { foodLogs: PropTypes.arrayOf(PropTypes.object) };

export default DayDrawerFoodLogCard;
